import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { MultifactorAuthentication, SessionUser, TokenValidity } from '../models';

export type RecaptchaVerifierSize = 'compact' | 'invisible' | 'normal';

export type RecaptchaVerifierTheme = 'dark' | 'light';

/**
 *  Abstract class to be extended by any authentication provider.
 *
 * @export
 */
@Injectable()
export abstract class AuthenticationService {
    /**
     * The method responsible to render the recaptcha verifier
     * @param container is the div's id where this recaptcha will be rendered
     * @param size can be 'compact', 'normal' or 'invisible', the default value is 'invisible'.
     * @param theme can be 'dark' or 'light', the default value is 'light'.
     */
    abstract renderRecaptchaVerifier(container: string, size?: RecaptchaVerifierSize, theme?: RecaptchaVerifierTheme): Promise<void>;

    /** The method responsible to clear the recaptcha verifier */
    abstract clearRecaptchaVerifier(): void;

    /** Asynchronous operation to send the verification code for the multi-factor authentication */
    abstract sendVerificationCodeAuth(): Promise<string>;

    /** The current multi-factor phone number */
    abstract getMultifactorPhoneNumber(): string;

    /** Asynchronous operation to authenticate the user using multi-factor authentication */
    abstract loginMultiFactorAuth(verificationCode: string, keepAuthenticated?: boolean): Promise<SessionUser>;

    /** The current authenticated user */
    abstract get user(): SessionUser;

    /** Method that provides an observable with the changes of the session user. */
    abstract sessionUserChanges(): Observable<SessionUser>;

    /**
     * The method responsible for checking whether the tenant uses 2FA and whether the user has a phoneNumber
     * @param email the email of the user to be verified
     */
    abstract useMultifactorAuthentication(email: string): Promise<MultifactorAuthentication>;

    /** checks if the user is online, i.e., if there is activity less than 'isAvailableInterval' seconds ago */
    abstract isUserAvailable(isAvailableInterval: number): boolean;

    /**
     * Asynchronous operation authenticate the user.
     *
     * @throws LoginError in case of invalid credentials/unknown error.
     */
    abstract login(
        username: string, 
        password: string, 
        keepAuthenticated?: boolean, 
        validateCredentialsOnly?: boolean
    ): Promise<SessionUser>;

    /** Asynchronous operation to logout the current authenticated user.  */
    abstract logout(releaseTicketsFromOperator?: boolean): Promise<any>;

    /** Used to finish the logout of the current authenticated user. */
    abstract finishLogout(): Promise<any>;

    /** Used to indicate when it is necessary to clear the data of the authenticated user. */
    abstract shouldClearUserSession(): Observable<boolean>;

    /** Asynchronous operation to indicate if the user is currently authenticated.  */
    abstract isAuthenticated(): Promise<boolean>;

    /** Asynchronous operation to indicate if is required to change the password of the currently authenticated user. */
    abstract passwordChangeRequired(): Promise<boolean>;

    /** Asynchronous operation to change the user password. */
    abstract changePassword(currentPassword: string, newPassword: string, newConfirmationPassword: string): Promise<void>;

    /** The method responsible for ending the login i.e. obtaining the sessionUser and indicating that the user is authenticated */
    abstract finishLoginFromStorage(keepAuthenticated?: boolean): Promise<SessionUser>;

    /** Asynchronous operation to reset a user password using the given token */
    abstract resetPassword(token: string, password: string, confirmationPassword: string): Promise<void>;

    /** Asynchronous operation to request a user password reset by sending a reset link to the user's associated e-mail. */
    abstract resetPasswordRequest(email: string): Promise<any>;

    /** Asynchronous operation to resend a confirmation email to the registered e-mail. */
    abstract resendConfirmationEmail(email: string): Promise<any>;

    /**
     *
     * Asynchronous operation to verify reset password token validity.
     * If it is valid, return also the user information.
     * If it is not "valid", the response will provide a code with the invalidity reason.
     *
     */
    abstract verifyResetPasswordTokenValidity(token: string): Promise<TokenValidity>;

    /**
     * invoked to update the user status
     * @param userId the id of the user
     * @param newStatusId new status id
     * @param registerMetric if should register corresponding metric event
     */
    abstract updateUserStatus(userId: string, newStatusId: string, registerMetric: boolean): Promise<void>;

    /** used to update the 'showAfterLoginStatusPrompt' flag */
    abstract updateLoginStatusPrompt(showPrompt: boolean): void;

    /** whether or not should display the after login status prompt */
    abstract loginStatusPromptObservable(): Observable<boolean>;

    /** fetch the tickets view corresponding to the tenantId of the authenticated user */
    abstract fetchTicketsViewConfiguration(tenantId: string): Promise<any>;
}
